import { makeAutoObservable, runInAction } from 'mobx';
import axios from 'axios';
import { message } from 'antd';
import { API_URL } from '../config';

function formatCountdown(seconds) {
    const total = Math.max(0, Number(seconds) || 0);
    const minutes = Math.floor(total / 60);
    const rest = total % 60;
    return `${minutes}:${String(rest).padStart(2, '0')}`;
}

function apiErrorMessage(error, fallback = 'Ошибка при загрузке заказов') {
    const data = error?.response?.data;
    const raw = data?.message ?? data?.detail ?? error?.message;
    if (typeof raw === 'string' && raw.trim()) {
        return raw;
    }
    if (Array.isArray(raw) && raw.length) {
        const first = raw[0];
        if (typeof first === 'string' && first.trim()) {
            return first;
        }
        if (first?.msg) {
            return String(first.msg);
        }
    }
    return fallback;
}

async function blobApiErrorMessage(error, fallback = 'Ошибка при загрузке этикетки') {
    const data = error?.response?.data;
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
        try {
            const parsed = JSON.parse(await data.text());
            const blobMessage = parsed?.message ?? parsed?.detail;
            if (typeof blobMessage === 'string' && blobMessage.trim()) {
                return blobMessage;
            }
        } catch {
            // Бинарный ответ без JSON-ошибки.
        }
    }
    return apiErrorMessage(error, fallback);
}

export const QUICK_FILTER_PRESETS = {
    all: { active: 'false', toExit: false },
    active: { active: 'true', toExit: false },
    current: { active: 'true', toExit: true },
    packaging: { active: 'true', toExit: false },
    shipping: { active: 'true', toExit: false },
    cancelled: { active: 'cancelled', toExit: false },
    delivering: { active: 'false', toExit: false },
};

const QUICK_FILTER_KEYS = Object.keys(QUICK_FILTER_PRESETS);

const cleanPosting = (order) => String(order?.posting_number || '').replace(/\n/g, '');

class OzonOrdersStore {
    orders = [];
    rawOrders = [];
    statistics = {};
    loading = false;
    selectedOrders = new Set();
    filters = {
        hist: 3, // Значение по умолчанию - 3 дня
        active: 'true',
        toExit: false
    };
    quickFilter = 'active';
    error = null;
    statusCounts = {};
    orderStatuses = {
        printedOrders: [],
        checkOrders: [],
        collectOrders: []
    };
    returnsInfo = {
        returns: {},
        issued: {},
    };

    statusPollingId = null;
    labelTimers = {};
    labelTimerRemaining = {};
    labelTimerSetAt = {};
    labelPrintTickId = null;
    labelGatePollId = null;

    constructor() {
        makeAutoObservable(this);
        this.initializeFilters();
        this.initializeOrderStatuses();
        this.initializeSelectedOrders();
    }

    initializeFilters() {
        let parsed = null;
        try {
            const savedFilters = localStorage.getItem('ozonFilters');
            if (savedFilters) {
                parsed = JSON.parse(savedFilters);
                this.filters = { ...this.filters, ...parsed };
            }
        } catch {
            parsed = null;
        }
        if (this.filters.active === true || this.filters.active === 'true') {
            this.filters.active = 'true';
        } else if (this.filters.active === false || this.filters.active === 'false') {
            this.filters.active = 'false';
        }
        this.filters.toExit = this.filters.toExit === true || this.filters.toExit === 'true';
        const hist = Number(this.filters.hist);
        this.filters.hist = Number.isFinite(hist) && hist > 0 ? hist : 3;
        if (parsed && QUICK_FILTER_KEYS.includes(parsed.quickFilter)) {
            this.quickFilter = parsed.quickFilter;
        } else if (parsed && Object.prototype.hasOwnProperty.call(parsed, 'quickFilter')) {
            this.quickFilter = null;
        } else {
            this.quickFilter = 'active';
            this.filters = { ...this.filters, ...QUICK_FILTER_PRESETS.active };
        }
        delete this.filters.quickFilter;
        this.persistFilters();
    }

    initializeOrderStatuses() {
        const savedStatuses = localStorage.getItem('orderStatuses');
        if (savedStatuses) {
            this.orderStatuses = JSON.parse(savedStatuses);
        }
    }

    initializeSelectedOrders() {
        const savedSelected = localStorage.getItem('selectedOrders');
        if (savedSelected) {
            this.selectedOrders = new Set(JSON.parse(savedSelected));
        }
    }

    startStatusPolling() {
        if (this.statusPollingId != null) return;
        this.fetchOrderStatuses();
        this.fetchReturns();
        this.fetchLabelPrintGate();
        this.statusPollingId = setInterval(() => {
            this.fetchOrderStatuses();
            this.fetchReturns();
            this.fetchLabelPrintGate();
        }, 10000);
    }

    stopStatusPolling() {
        if (this.statusPollingId == null) return;
        clearInterval(this.statusPollingId);
        this.statusPollingId = null;
    }

    get labelPrintRemainingSeconds() {
        const values = Object.values(this.labelTimerRemaining);
        return values.length ? Math.max(...values) : 0;
    }

    get isLabelPrintLocked() {
        return this.labelPrintRemainingSeconds > 0;
    }

    get labelPrintCountdown() {
        return formatCountdown(this.labelPrintRemainingSeconds);
    }

    labelPrintRemainingFor = (postingNumber) =>
        this.labelTimerRemaining[cleanPosting({ posting_number: postingNumber })] || 0;

    isLabelPrintLockedFor = (postingNumber) => this.labelPrintRemainingFor(postingNumber) > 0;

    labelPrintCountdownFor = (postingNumber) =>
        formatCountdown(this.labelPrintRemainingFor(postingNumber));

    applyLabelPrintGate = (data, { merge = false, requestedAt = 0 } = {}) => {
        const postings = data?.postings || {};
        const now = Date.now();
        const next = {};
        const setAt = {};
        if (merge) {
            Object.assign(next, this.labelTimers);
            Object.assign(setAt, this.labelTimerSetAt);
        } else {
            Object.entries(this.labelTimers).forEach(([number, availableAt]) => {
                if ((this.labelTimerSetAt[number] || 0) > requestedAt && availableAt > now) {
                    next[number] = availableAt;
                    setAt[number] = this.labelTimerSetAt[number];
                }
            });
        }
        Object.entries(postings).forEach(([number, seconds]) => {
            const remaining = Math.max(0, Number(seconds) || 0);
            const key = cleanPosting({ posting_number: number });
            if (remaining > 0) {
                next[key] = now + remaining * 1000;
                setAt[key] = now;
            }
        });
        runInAction(() => {
            this.labelTimers = next;
            this.labelTimerSetAt = setAt;
        });
        this.tickLabelTimers();
        this.startLabelPrintCountdown();
    };

    tickLabelTimers = () => {
        const now = Date.now();
        const remaining = {};
        const timers = {};
        Object.entries(this.labelTimers).forEach(([number, availableAt]) => {
            const seconds = Math.max(0, Math.ceil((availableAt - now) / 1000));
            if (seconds > 0) {
                remaining[number] = seconds;
                timers[number] = availableAt;
            }
        });
        const prev = this.labelTimerRemaining;
        const prevKeys = Object.keys(prev);
        const changed =
            prevKeys.length !== Object.keys(remaining).length ||
            prevKeys.some((number) => prev[number] !== remaining[number]);
        runInAction(() => {
            if (changed) {
                this.labelTimerRemaining = remaining;
            }
            if (Object.keys(timers).length !== Object.keys(this.labelTimers).length) {
                this.labelTimers = timers;
            }
        });
        if (!Object.keys(timers).length) {
            this.stopLabelPrintCountdown();
        }
    };

    startLabelPrintCountdown = () => {
        if (!Object.keys(this.labelTimers).length) {
            this.stopLabelPrintCountdown();
            return;
        }
        if (this.labelPrintTickId == null) {
            this.labelPrintTickId = setInterval(this.tickLabelTimers, 250);
        }
        if (this.labelGatePollId == null) {
            this.labelGatePollId = setInterval(() => this.fetchLabelPrintGate(), 3000);
        }
    };

    stopLabelPrintCountdown = () => {
        if (this.labelPrintTickId != null) {
            clearInterval(this.labelPrintTickId);
            this.labelPrintTickId = null;
        }
        if (this.labelGatePollId != null) {
            clearInterval(this.labelGatePollId);
            this.labelGatePollId = null;
        }
    };

    async fetchLabelPrintGate() {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            const requestedAt = Date.now();
            const response = await axios.get(`${API_URL}/v1/orders/ozon/label-print-gate`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            this.applyLabelPrintGate(response.data, { requestedAt });
        } catch (error) {
            console.error('Error fetching label print gate:', error);
        }
    }

    assertLabelPrintAvailable(postingNumber) {
        const locked = postingNumber
            ? this.isLabelPrintLockedFor(postingNumber)
            : this.isLabelPrintLocked;
        if (!locked) return;
        const countdown = postingNumber
            ? this.labelPrintCountdownFor(postingNumber)
            : this.labelPrintCountdown;
        throw new Error(
            `Печать этикеток будет доступна через ${countdown}. Ozon готовит файлы после сборки.`
        );
    }

    async fetchOrderStatuses() {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/v1/status/getAll`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            runInAction(() => {
                if (response.data?.data) {
                    this.applyStatusLists(response.data.data);
                }
            });
        } catch (error) {
            console.error('Error fetching order statuses:', error);
        }
    }

    applyStatusLists = (data) => {
        this.orderStatuses = {
            printedOrders: data?.PrintedOrders || [],
            checkOrders: data?.CheckOrders || [],
            collectOrders: data?.CollectOrders || [],
        };
        localStorage.setItem('orderStatuses', JSON.stringify(this.orderStatuses));
        this.applyFilters();
    };

    async fetchReturns() {
        try {
            const response = await axios.get(`${API_URL}/v1/returns/summary`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            runInAction(() => {
                this.returnsInfo = {
                    returns: response.data?.returns || {},
                    issued: response.data?.issued || {},
                };
            });
        } catch (error) {
            console.error('Error fetching returns:', error);
        }
    }

    async issueReturn(postingNumber, article) {
        const cleanPostingNumber = String(postingNumber || '').replace(/\n/g, '');
        const response = await axios.post(
            `${API_URL}/v1/returns/issue`,
            { posting_number: cleanPostingNumber, article },
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        runInAction(() => {
            const returns = { ...this.returnsInfo.returns };
            const quantity = Number(response.data?.quantity) || 0;
            if (quantity > 0) {
                returns[article] = quantity;
            } else {
                delete returns[article];
            }
            const issuedForPosting = this.returnsInfo.issued[cleanPostingNumber] || [];
            this.returnsInfo = {
                returns,
                issued: {
                    ...this.returnsInfo.issued,
                    [cleanPostingNumber]: [...issuedForPosting, article],
                },
            };
        });
        return response.data;
    }

    async togglePrinted(postingNumber) {
        const token = localStorage.getItem('token');
        const cleanPostingNumber = String(postingNumber || '').replace(/\n/g, '');
        const response = await axios.get(`${API_URL}/v1/status/togglePrinted`, {
            params: { PostingNumber: cleanPostingNumber },
            headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data?.data) {
            runInAction(() => this.applyStatusLists(response.data.data));
        }
        return this.orderStatuses.printedOrders.includes(cleanPostingNumber);
    }

    async toggleCollected(postingNumber) {
        const token = localStorage.getItem('token');
        const cleanPostingNumber = String(postingNumber || '').replace(/\n/g, '');
        const response = await axios.get(`${API_URL}/v1/status/toggleCollected`, {
            params: { PostingNumber: cleanPostingNumber },
            headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data?.data) {
            runInAction(() => this.applyStatusLists(response.data.data));
        }
        return this.orderStatuses.collectOrders.includes(cleanPostingNumber);
    }

    persistFilters = () => {
        localStorage.setItem(
            'ozonFilters',
            JSON.stringify({
                ...this.filters,
                quickFilter: this.quickFilter,
            })
        );
    };

    setFilters = (newFilters) => {
        this.filters = { ...this.filters, ...newFilters };
        if ('active' in newFilters || 'toExit' in newFilters) {
            this.quickFilter = null;
        }
        this.persistFilters();

        if ('hist' in newFilters) {
            this.fetchOrders();
        } else {
            this.applyFilters();
        }
    };

    setQuickFilter = (key) => {
        const preset = QUICK_FILTER_PRESETS[key];
        if (!preset) return;
        this.quickFilter = key;
        this.filters = { ...this.filters, ...preset };
        this.persistFilters();
        this.applyFilters();
    };

    applyFilters = () => {
        let filteredOrders = [...this.rawOrders];
        const checkOrders = new Set(
            (this.orderStatuses.checkOrders || []).map((value) => String(value))
        );
        const collectOrders = new Set(
            (this.orderStatuses.collectOrders || []).map((value) => String(value))
        );
        const quickFilter = this.quickFilter;

        if (quickFilter === 'all') {
            // без дополнительного фильтра по статусу
        } else if (quickFilter === 'active') {
            filteredOrders = filteredOrders.filter(
                (order) =>
                    order.status === 'awaiting_packaging' || order.status === 'awaiting_deliver'
            );
        } else if (quickFilter === 'current') {
            filteredOrders = filteredOrders.filter((order) => {
                const status = order.status;
                return (
                    (status === 'awaiting_packaging' || status === 'awaiting_deliver') &&
                    !checkOrders.has(cleanPosting(order))
                );
            });
        } else if (quickFilter === 'packaging') {
            filteredOrders = filteredOrders.filter(
                (order) => order.status === 'awaiting_packaging'
            );
        } else if (quickFilter === 'shipping') {
            filteredOrders = filteredOrders.filter(
                (order) => order.status === 'awaiting_deliver'
            );
        } else if (quickFilter === 'cancelled') {
            filteredOrders = filteredOrders.filter((order) => order.status === 'cancelled');
        } else if (quickFilter === 'delivering') {
            filteredOrders = filteredOrders.filter((order) => order.status === 'delivering');
        } else if (this.filters.active === 'true') {
            filteredOrders = filteredOrders.filter((order) =>
                ['awaiting_registration', 'awaiting_deliver', 'awaiting_packaging'].includes(
                    order.status
                )
            );
        } else if (this.filters.active === 'cancelled') {
            filteredOrders = filteredOrders.filter((order) =>
                ['cancelled', 'not_accepted'].includes(order.status)
            );
        }

        if (this.filters.toExit) {
            filteredOrders = filteredOrders.filter(
                (order) => !collectOrders.has(cleanPosting(order))
            );
        }

        this.orders = filteredOrders;
    };

    ensureOrdersLoaded = async () => {
        if (this.rawOrders.length > 0 || this.loading) {
            return false;
        }
        await this.fetchOrders();
        return true;
    };

    fetchOrders = async () => {
        try {
            this.loading = true;
            this.error = null;
            
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('Не найден токен авторизации');
            }

            const params = {
                hist: this.filters.hist,
                toExit: false,
                active: false,
            };

            const response = await axios.get(`${API_URL}/v1/orders/ozon/get_orders`, {
                params,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            runInAction(() => {
                if (response.data) {
                    this.rawOrders = response.data.orders || [];
                    this.statistics = response.data.statistics || {};
                    this.applyFilters();
                } else {
                    throw new Error('Получен пустой ответ от сервера');
                }
                this.loading = false;
            });
        } catch (error) {
            const text = apiErrorMessage(error);
            runInAction(() => {
                this.error = text;
                this.loading = false;
                this.orders = [];
                this.rawOrders = [];
                this.statistics = {};
                
                if (error.response?.status === 401) {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                }
            });
            if (error.response?.status !== 401) {
                message.error({
                    content: text,
                    key: 'loading',
                    duration: 8,
                });
            }
        }
    };

    get orderStatusCounts() {
        return {
            awaiting_packaging: this.orders.filter(order => order.status === 'awaiting_packaging').length,
            awaiting_deliver: this.orders.filter(order => order.status === 'awaiting_deliver').length,
            delivering: this.orders.filter(order => order.status === 'delivering').length,
            delivered: this.orders.filter(order => order.status === 'delivered').length,
            cancelled: this.orders.filter(order => order.status === 'cancelled').length
        };
    }

    get quickFilterCounts() {
        const checkOrders = new Set(
            (this.orderStatuses.checkOrders || []).map((value) => String(value))
        );
        const collectOrders = new Set(
            (this.orderStatuses.collectOrders || []).map((value) => String(value))
        );
        const all = this.rawOrders || [];
        const notCollected = (order) => !collectOrders.has(cleanPosting(order));
        const current = all.filter((order) => {
            const status = order.status;
            return (
                (status === 'awaiting_packaging' || status === 'awaiting_deliver') &&
                !checkOrders.has(cleanPosting(order))
            );
        });
        return {
            all: all.length,
            active: all.filter(
                (order) =>
                    order.status === 'awaiting_packaging' || order.status === 'awaiting_deliver'
            ).length,
            current: current.filter(notCollected).length,
            packaging: all.filter((order) => order.status === 'awaiting_packaging').length,
            shipping: all.filter((order) => order.status === 'awaiting_deliver').length,
            cancelled: all.filter((order) => order.status === 'cancelled').length,
            delivering: all.filter((order) => order.status === 'delivering').length,
        };
    }

    updateOrderStatus = (postingNumber, newStatus) => {
        const patch = (list) =>
            list.map((order) =>
                order.posting_number === postingNumber ? { ...order, status: newStatus } : order
            );
        runInAction(() => {
            this.rawOrders = patch(this.rawOrders);
            this.applyFilters();
        });
    };

    collectOrder = async (postingNumber) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('Не найден токен авторизации');
            }

            const response = await axios.get(`${API_URL}/v1/orders/ozon/collect`, {
                params: { PostingNumber: postingNumber },
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.statusCode === 200 && response.data.body.result) {
                this.updateOrderStatus(postingNumber, 'awaiting_deliver');
                this.applyLabelPrintGate(response.data.label_print_gate, { merge: true });
                return { success: true, message: response.data.message || 'Отправление собрано' };
            } else {
                throw new Error(response.data.message || 'Ошибка при сборке заказа');
            }
        } catch (error) {
            console.error('Error collecting order:', error);
            throw error;
        }
    };

    async printPostingLabel(postingNumber) {
        try {
            this.assertLabelPrintAvailable(postingNumber);
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('Не найден токен авторизации');
            }

            const response = await axios.get(`${API_URL}/v1/orders/ozon/BuildPostingLabel`, {
                params: {
                    PostingNumber: postingNumber,
                    willDownloadLabel: true,
                    clearOldLabel: false
                },
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                responseType: 'blob'
            });

            return await this.saveLabelPdf(response, `label-${postingNumber}.pdf`);
        } catch (error) {
            console.error('Error printing label:', error);
            throw new Error(await blobApiErrorMessage(error, error.message || 'Ошибка при загрузке этикетки'));
        }
    }

    async saveLabelPdf(response, filename) {
        const data = response.data;
        const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
        const signature = blob.size > 0 ? await blob.slice(0, 5).text() : '';
        if (signature !== '%PDF-') {
            throw new Error('Сервер вернул некорректный файл этикетки, отметка о печати не поставлена');
        }

        const url = window.URL.createObjectURL(
            blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' })
        );
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);

        const splitHeader = (name) =>
            String(response.headers?.[name] || '')
                .split(',')
                .map((value) => value.trim())
                .filter(Boolean);
        const postings = splitHeader('x-label-postings');
        const withErrors = new Set(splitHeader('x-label-errors'));
        const toMark = postings.filter((number) => !withErrors.has(number));

        if (toMark.length) {
            try {
                const marked = await axios.post(
                    `${API_URL}/v1/status/markPrinted`,
                    { posting_numbers: toMark },
                    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                );
                if (marked.data?.data) {
                    runInAction(() => this.applyStatusLists(marked.data.data));
                }
            } catch (error) {
                console.error('Error marking printed:', error);
            }
        }

        if (withErrors.size) {
            return {
                success: true,
                warning: true,
                unmarked: [...withErrors],
                message: `Файл скачан, но при сборке этикеток были ошибки: ${[...withErrors].join(', ')}. Они не отмечены распечатанными`,
            };
        }
        return {
            success: true,
            message: postings.length > 1 ? 'Этикетки успешно загружены' : 'Этикетка успешно загружена',
        };
    }

    async RePrintPostingLabel(postingNumber) {
        try {
            this.assertLabelPrintAvailable(postingNumber);
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('Не найден токен авторизации');
            }

            const response = await axios.get(`${API_URL}/v1/orders/ozon/BuildPostingLabel`, {
                params: {
                    PostingNumber: postingNumber,
                    willDownloadLabel: true,
                    clearOldLabel: true
                },
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                responseType: 'blob'
            });

            return await this.saveLabelPdf(response, `label-${postingNumber}.pdf`);
        } catch (error) {
            console.error('Error printing label:', error);
            throw new Error(await blobApiErrorMessage(error, error.message || 'Ошибка при загрузке этикетки'));
        }
    }

        async downloadSelectedLabels() {
        try {
            this.assertLabelPrintAvailable();
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('Не найден токен авторизации');
            }

            const selectedPostingNumbers = Array.from(this.selectedOrders);
            
            const response = await axios.post(`${API_URL}/v1/orders/ozon/GetPostingLabelByNumberArray`, 
                {
                    posting_numbers: selectedPostingNumbers,
                    will_download_label: true,
                    clear_old_label: false
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    responseType: 'blob'
                }
            );

            const now = new Date();
            const formatted = now.toLocaleDateString('ru-RU').split('.').join('_') + '_' + 
                            now.toLocaleTimeString('ru-RU').split(':').join('_');
            return await this.saveLabelPdf(
                response,
                `${formatted}_Selected_${selectedPostingNumbers.length}.pdf`
            );
        } catch (error) {
            console.error('Error downloading labels:', error);
            throw new Error(await blobApiErrorMessage(error, error.message || 'Ошибка при загрузке этикеток'));
        }
    }

    async downloadLabelsByDate(date) {
        try {
            this.assertLabelPrintAvailable();
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('Не найден токен авторизации');
            }

            // Фильтруем заказы по выбранной дате
            const selectedPostingNumbers = this.orders
                .filter(order => order.in_process_at.split('\n')[0] === date)
                .map(order => order.posting_number);

            if (selectedPostingNumbers.length === 0) {
                throw new Error('Нет заказов на выбранную дату');
            }

            const response = await axios.post(`${API_URL}/v1/orders/ozon/GetPostingLabelByNumberArray`, 
                {
                    posting_numbers: selectedPostingNumbers,
                    will_download_label: true,
                    clear_old_label: false
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    responseType: 'blob'
                }
            );

            const now = new Date();
            const formatted = now.toLocaleDateString('ru-RU').split('.').join('_') + '_' + 
                            now.toLocaleTimeString('ru-RU').split(':').join('_');
            return await this.saveLabelPdf(
                response,
                `${formatted}_ByDate_${selectedPostingNumbers.length}.pdf`
            );
        } catch (error) {
            console.error('Error downloading labels:', error);
            throw new Error(await blobApiErrorMessage(error, error.message || 'Ошибка при загрузке этикеток'));
        }
    }

    async downloadUnprintedLabels() {
        try {
            this.assertLabelPrintAvailable();
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('Не найден токен авторизации');
            }

            // Фильтруем нераспечатанные заказы
            const selectedPostingNumbers = this.orders
                .filter(order => {
                    const cleanPostingNumber = order.posting_number.replace(/\n/g, '');
                    return !this.orderStatuses.printedOrders.includes(cleanPostingNumber) && 
                           !this.orderStatuses.collectOrders.includes(cleanPostingNumber);
                })
                .map(order => order.posting_number);

            if (selectedPostingNumbers.length === 0) {
                throw new Error('Нет нераспечатанных заказов');
            }

            const response = await axios.post(`${API_URL}/v1/orders/ozon/GetPostingLabelByNumberArray`, 
                {
                    posting_numbers: selectedPostingNumbers,
                    will_download_label: true,
                    clear_old_label: false
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    responseType: 'blob'
                }
            );

            const now = new Date();
            const formatted = now.toLocaleDateString('ru-RU').split('.').join('_') + '_' + 
                            now.toLocaleTimeString('ru-RU').split(':').join('_');
            return await this.saveLabelPdf(
                response,
                `${formatted}_Unprinted_${selectedPostingNumbers.length}.pdf`
            );
        } catch (error) {
            console.error('Error downloading labels:', error);
            throw new Error(await blobApiErrorMessage(error, error.message || 'Ошибка при загрузке этикеток'));
        }
    }

    toggleOrderSelection = (postingNumber) => {
        if (this.selectedOrders.has(postingNumber)) {
            this.selectedOrders.delete(postingNumber);
        } else {
            this.selectedOrders.add(postingNumber);
        }
        localStorage.setItem('selectedOrders', JSON.stringify([...this.selectedOrders]));
    };

    isOrderSelected = (postingNumber) => {
        return this.selectedOrders.has(postingNumber);
    };

    clearSelectedOrders = () => {
        this.selectedOrders.clear();
        localStorage.removeItem('selectedOrders');
    };

    updateProductPresent = (articlePresentMap) => {
        const updates = articlePresentMap instanceof Map
            ? articlePresentMap
            : new Map(Object.entries(articlePresentMap || {}));
        if (!updates.size) return;

        const patchOrders = (list) =>
            list.map((order) => ({
                ...order,
                products: (order.products || []).map((product) => {
                    const offerId = product.offer_id || product?.color?._id;
                    if (!offerId || !updates.has(offerId)) return product;
                    return { ...product, present: updates.get(offerId) };
                }),
            }));

        runInAction(() => {
            this.orders = patchOrders(this.orders);
            this.rawOrders = patchOrders(this.rawOrders);
        });
    };

    getUncheckedHobaBuffs = () => {
        let hoba = 0;
        let buff = 0;
        const checkOrders = this.orderStatuses?.checkOrders || [];
        this.orders.forEach((order) => {
            const cleanPostingNumber = String(order.posting_number || '').replace(/\n/g, '');
            if (
                !(
                    order.status === 'awaiting_packaging' ||
                    order.status === 'awaiting_deliver'
                ) ||
                checkOrders.includes(cleanPostingNumber)
            ) {
                return;
            }
            (order.products || []).forEach((product) => {
                const offerId = product.offer_id || product?.color?._id || '';
                if (String(offerId).startsWith('HB-')) {
                    hoba += 1;
                } else if (String(offerId).startsWith('BN-')) {
                    buff += 1;
                }
            });
        });
        return { hoba, buff, total: hoba + buff };
    };
}

// Экспортируем класс
export { OzonOrdersStore };

// Создаем и экспортируем инстанс store
const store = new OzonOrdersStore();
export default store; 